import { t } from "adapters/i18n";
import { AxiosError } from "axios";

/**
 * Checks if the error is an AxiosError with response data.
 */
function isAxiosErrorWithData(error: unknown): error is AxiosError {
  const axiosError = error as AxiosError;

  return (
    axiosError != null &&
    typeof axiosError === "object" &&
    "response" in axiosError &&
    axiosError.response != null &&
    typeof axiosError.response === "object" &&
    "data" in axiosError.response &&
    axiosError.response.data != null &&
    typeof axiosError.response.data === "object"
  );
}

/**
 * Extracts message from AxiosError response data.
 */
function getAxiosErrorMessage(error: AxiosError): string | null {
  const data = error.response?.data;

  if (!data || typeof data !== "object") {
    return null;
  }

  if ("mensagem" in data && typeof data.mensagem === "string") {
    return data.mensagem;
  }

  if ("message" in data && typeof data.message === "string") {
    return data.message;
  }

  return null;
}

/**
 * Checks if error is an object with a message property.
 */
function hasMessageProperty(error: unknown): error is { message: string } {
  return (
    error != null &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  );
}

/**
 * Extracts a user-friendly error message from various error types.
 * @param error - The error object to extract the message from.
 * @returns A string containing the error message.
 */
export function getErrorMessage(
  error: unknown,
  fallbackMessage?: string
): string {
  const defaultMessage: string =
    fallbackMessage ?? t("common:unknown-error-occurred");

  if (isAxiosErrorWithData(error)) {
    const axiosMessage = getAxiosErrorMessage(error);
    if (axiosMessage) {
      return axiosMessage;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (hasMessageProperty(error)) {
    return error.message;
  }

  return defaultMessage;
}
